"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import sampleRoomsData from "@/fixtures/sample-rooms.json";
import { SampleRoom } from "@/types";
import {
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Camera,
  Sliders,
  ShoppingBag,
} from "lucide-react";
import { formatGBP } from "@/lib/utils";

export default function LandingPage() {
  const sampleRooms = sampleRoomsData as SampleRoom[];
  const [activeSampleIndex, setActiveSampleIndex] = useState(0);
  const currentRoom = sampleRooms[activeSampleIndex] || sampleRooms[0];

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Header />

      <main className="flex-1">
        {/* ========================================================================= */}
        {/* S1: HERO SECTION                                                          */}
        {/* ========================================================================= */}
        <section className="relative pt-6 pb-16 sm:pt-12 sm:pb-24 px-4 sm:px-6 max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <Badge variant="clay" className="mb-4">
              <Sparkles className="w-3 h-3 text-clay" />
              <span>Launch Edition · Free Redesigns</span>
            </Badge>

            <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-ink leading-[1.08]">
              Your room, <span className="italic font-normal">reimagined</span>. <br />
              Every piece <span className="text-clay">real</span>.
            </h1>

            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-ink-soft max-w-2xl mx-auto">
              Upload a photo of your room. Get a designer-quality redesign where
              selected furniture comes from real UK product catalogs, matched to
              your budget.
            </p>

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link href="/design" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto gap-2">
                  <span>Design my room — free</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>

              <a href="#how-it-works" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  See how it works
                </Button>
              </a>
            </div>

            {/* Proof strip */}
            <div className="mt-6 flex items-center justify-center gap-4 text-xs font-medium text-ink-soft">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-sage" />
                <span>{formatGBP(currentRoom.total_price_gbp)} total look</span>
              </span>
              <span>•</span>
              <span>{currentRoom.item_count} matched items</span>
              <span>•</span>
              <span className="text-sage font-semibold">In stock at last check</span>
            </div>
          </div>

          {/* Hero Before/After Interactive Slider */}
          <div className="max-w-5xl mx-auto">
            <BeforeAfterSlider
              beforeImage={currentRoom.before_image}
              afterImage={currentRoom.after_image}
              tags={currentRoom.tags}
              defaultPosition={55}
            />
          </div>
        </section>

        {/* ========================================================================= */}
        {/* S2: "TRY IT" PLAYGROUND SECTION                                           */}
        {/* ========================================================================= */}
        <section id="styles" className="py-16 sm:py-20 bg-paper-deep border-y border-line px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-xl mx-auto mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                Explore sample transformations
              </h2>
              <p className="text-sm text-ink-soft mt-2">
                Click any sample room below to see how our AI matches real products.
              </p>
            </div>

            {/* Room Selector Tabs */}
            <div className="flex justify-center gap-3 mb-8 overflow-x-auto pb-2">
              {sampleRooms.map((room, idx) => (
                <button
                  key={room.id}
                  onClick={() => setActiveSampleIndex(idx)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeSampleIndex === idx
                      ? "bg-ink text-paper shadow-sm"
                      : "bg-paper text-ink-soft hover:text-ink border border-line"
                  }`}
                >
                  {room.title} ({room.style})
                </button>
              ))}
            </div>

            {/* Small card preview list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {currentRoom.tags.map((tag) => (
                <div
                  key={tag.id}
                  className="bg-paper p-4 rounded-xl border border-line flex items-center justify-between shadow-soft"
                >
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-clay">
                      {tag.category}
                    </span>
                    <h4 className="text-sm font-semibold text-ink mt-0.5">
                      {tag.product_title || tag.label}
                    </h4>
                    <span className="text-xs text-ink-soft">{tag.retailer}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-mono font-bold text-brass-deep">
                      {formatGBP(tag.price_gbp)}
                    </span>
                    <span className="block text-[10px] text-sage font-medium">Check availability</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================================================================= */}
        {/* S3: MECHANISM / HOW IT WORKS                                              */}
        {/* ========================================================================= */}
        <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 max-w-6xl mx-auto">
          <div className="text-center max-w-xl mx-auto mb-16">
            <Badge variant="paper" className="mb-3">
              <span>The Mechanism</span>
            </Badge>
            <h2 className="text-3xl font-bold text-ink">
              Not AI fantasy furniture. <br />
              Everything is actually for sale.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-paper border border-line shadow-soft flex flex-col">
              <div className="w-12 h-12 rounded-full bg-clay/10 text-clay flex items-center justify-center mb-4">
                <Camera className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">1. Snap your room</h3>
              <p className="text-sm text-ink-soft leading-relaxed">
                Take a quick photo from the doorway on your phone. We preserve your
                windows, doors, floor, and architecture.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-paper border border-line shadow-soft flex flex-col">
              <div className="w-12 h-12 rounded-full bg-clay/10 text-clay flex items-center justify-center mb-4">
                <Sliders className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">2. Set your style & budget</h3>
              <p className="text-sm text-ink-soft leading-relaxed">
                A 5-tap visual quiz captures your aesthetic preferences, budget band,
                and any existing pieces you want to keep.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-paper border border-line shadow-soft flex flex-col">
              <div className="w-12 h-12 rounded-full bg-clay/10 text-clay flex items-center justify-center mb-4">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">3. Get the exact shopping list</h3>
              <p className="text-sm text-ink-soft leading-relaxed">
                Our engine re-renders your room using real UK catalog products. Tap any
                item to see the exact price, dimensions, and buy link.
              </p>
            </div>
          </div>
        </section>

        {/* ========================================================================= */}
        {/* S4: HUMAN TRUST SECTION                                                   */}
        {/* ========================================================================= */}
        <section id="consult" className="py-16 bg-paper-deep border-t border-line px-4 sm:px-6">
          <div className="max-w-4xl mx-auto rounded-3xl bg-paper p-8 sm:p-12 border border-line shadow-card flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <Badge variant="clay" className="mb-3">
                <ShieldCheck className="w-3.5 h-3.5 text-clay" />
                <span>Professional Oversight</span>
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                Checked by an interior designer with 11 years London experience.
              </h2>
              <p className="text-sm text-ink-soft mt-3 leading-relaxed">
                AI gives you instant speed, but human judgement ensures spatial
                harmony. Book an optional 45-minute remote consult with our lead
                designer to refine and sanity-check your shopping list.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <Button variant="brass" size="md" disabled className="opacity-50 cursor-not-allowed">
                  Book a consult (Coming Soon)
                </Button>
              </div>
            </div>
            <div className="w-full md:w-64 bg-paper-deep p-4 rounded-2xl border border-line text-xs space-y-2.5 text-ink-soft">
              <div className="flex items-center gap-2 font-medium text-ink">
                <CheckCircle2 className="w-4 h-4 text-sage" />
                <span>Live video scheme review</span>
              </div>
              <div className="flex items-center gap-2 font-medium text-ink">
                <CheckCircle2 className="w-4 h-4 text-sage" />
                <span>Written designer notes in 48h</span>
              </div>
              <div className="flex items-center gap-2 font-medium text-ink">
                <CheckCircle2 className="w-4 h-4 text-sage" />
                <span>Alternative trade finishes</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
